import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/core";
import { Camera, CameraView } from "expo-camera";
import React, { useEffect, useState } from "react";
import {
	ActivityIndicator,
	StyleSheet,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Button, Slider } from "react-native-elements";
import SafeAreaView from "react-native-safe-area-view";
import { useDispatch } from "react-redux";
import {
	SET_CHAT_PHOTO_DATA,
	SET_DROP_DOWN_ALERT_INFO,
	SET_PHOTO_URI,
	SET_POST_PHOTO_DATA,
	SET_TEMPLATE_PHOTO_DATA
} from "../actions/types";
import ActionButton from "../components/actionButton/ActionButton";
import { t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	COLOR5,
	DARKGREY_COLOR,
	SCREEN_HEIGHT,
	SCREEN_WIDTH,
	isAndroid,
	isNative,
	isWeb,
	isiOS
} from "../styles/commonStyles";
import { StatusBar } from "expo-status-bar";

const gridSize =
	SCREEN_WIDTH > SCREEN_HEIGHT ? SCREEN_HEIGHT - 300 : SCREEN_WIDTH - 20;

const screenParams = {
	chat: {
		showGridParam: false,
		type: SET_CHAT_PHOTO_DATA,
		showZoom: true,
		cameraTypeParam: "back"
	},
	post: {
		type: SET_POST_PHOTO_DATA,
		showZoom: true,
		cameraTypeParam: "back"
	},
	template: {
		type: SET_TEMPLATE_PHOTO_DATA,
		showZoom: true,
		cameraTypeParam: "back"
	},
	profile: {
		showGridParam: true,
		type: SET_PHOTO_URI,
		cameraTypeParam: "front"
	}
};

const Grid = () => {
	const { justifyContentCenter, alignItemsCenter, flex1 } = commonStyles;

	return (
		<View style={[flex1, alignItemsCenter, justifyContentCenter]}>
			<View style={styles.grid}>
				<View style={styles.row}>
					<View style={styles.cell} />
					<View style={styles.cell} />
				</View>
				<View style={styles.row}>
					<View style={styles.cell} />
					<View style={styles.cell} />
				</View>
				<View style={styles.row}>
					<View style={styles.cell} />
					<View style={styles.cell} />
				</View>
			</View>
		</View>
	);
};

export default function CameraScreen(props) {
	const {
		justifyContentCenter,
		alignItemsCenter,
		bgColor1,
		bgColor2,
		flex1,
		p10,
		w100p,
		bgWhite,
		opacity05,
		opacity1,
		fontBold,
		fs16,
		fs18,
		red,
		darkgrey,
		mb20
	} = commonStyles;

	let camera;

	const {
		cameraTypeParam,
		type,
		showGridParam,
		showZoom = false
	} = screenParams[props.route?.params.from || "profile"];

	const dispatch = useDispatch();
	const navigation = useNavigation();

	const [hasPermission, setHasPermission] = useState(null);
	const [showGrid, setShowGrid] = useState(true);
	const [showSwitchCamera, setShowSwitchCamera] = useState(true);
	const [bestRatio, setBestRatio] = useState("2:1");
	const [flashMode, setFlashMode] = useState("off");
	const [isReady, setReady] = useState(null);
	const [isCapturing, setCaturing] = useState(false);
	const [isMenuOpen, setMenuOpen] = useState(false);
	const [cameraType, setCameraType] = useState("back");
	const [zoom, setZoom] = useState(0);

	useEffect(() => {
		if (isReady) {
			(async () => {
				const { status } = await Camera.requestCameraPermissionsAsync();
				if (status !== "granted") {
					dispatch({
						type: SET_DROP_DOWN_ALERT_INFO,
						info: "camerainfo"
					});
				}
				setHasPermission(status === "granted");
			})();
		} else {
			setHasPermission(false);
		}
	}, [isReady]);

	useEffect(() => {
		if (isCapturing) {
			(async () => {
				await capture();
			})();
		}
	}, [isCapturing]);

	const onCameraReady = async () => {
		// compute best ratio for android
		if (isAndroid && !bestRatio) {
			const wantedRatio = SCREEN_HEIGHT / SCREEN_WIDTH;
			const ratios = await camera.getSupportedRatiosAsync();
			let bestRatio = 0;
			let bestRatioError = 100000;
			for (const ratio of ratios) {
				const r = ratio.split(":");
				if (Math.abs(wantedRatio - r[0] / r[1]) < bestRatioError) {
					bestRatioError = Math.abs(wantedRatio - r[0] / r[1]);
					bestRatio = ratio;
				}
			}
			setBestRatio(bestRatio);
		}
	};

	const capture = async () => {
		let photoData = await camera.takePictureAsync({
			quality: 0.5,
			base64: true
		});

		const { base64, uri } = photoData;

		const files = [];
		if (isWeb) {
			files.push({
				name: "" + Date.now(),
				base64: uri
			});
		} else {
			const name = uri.substring(
				uri.lastIndexOf("/") + 1,
				uri.lastIndexOf(".")
			);
			files.push({
				name,
				base64: "data:image/jpeg;base64," + base64
			});
		}

		dispatch({
			type,
			value: uri,
			files
		});
		navigation.goBack();
	};

	if (!isReady) {
		setCameraType(cameraTypeParam);
		setShowGrid(showGridParam);
		setReady(true);
	}

	if (hasPermission === null) {
		return <View />;
	} else if (!hasPermission) {
		return (
			<View style={[flex1, bgColor1]}>
				<View style={[flex1, alignItemsCenter, justifyContentCenter]}>
					<Text style={[fontBold, red, fs18]}>
						{t("profile:nocameraaccess")}
					</Text>
					<Button
						type="clear"
						activeOpacity={0.8}
						title={t("button:cancel")}
						onPress={() => navigation.goBack()}
						titleStyle={[darkgrey, fontBold, fs18]}
					/>
				</View>
			</View>
		);
	} else {
		return (
			<SafeAreaView
				style={[
					flex1,
					isCapturing && bgWhite,
					isCapturing ? opacity05 : opacity1
				]}
			>
				<StatusBar barStyle="dark-content" />
				{isCapturing && (
					<View style={styles.loading_container}>
						<ActivityIndicator size="large" color={COLOR2} />
					</View>
				)}
				<CameraView
					ref={cameraRef => (camera = cameraRef)}
					style={[flex1]}
					facing={cameraType}
					autofocus="on"
					flash={flashMode}
					onCameraReady={async () => {
						await onCameraReady();
					}}
					zoom={zoom}
					ratio={bestRatio}
				>
					{showGrid && isiOS && <Grid />}
					<SafeAreaView
						style={[
							flex1,
							{ position: "absolute", bottom: 20 },
							w100p,
							alignItemsCenter
						]}
					>
						{showZoom && (
							<View style={[w100p, mb20]}>
								<Slider
									style={[{ marginHorizontal: 30 }]}
									value={zoom}
									thumbTintColor={COLOR2}
									onValueChange={zoom => setZoom(zoom)}
								/>
							</View>
						)}
						<TouchableOpacity
							disabled={isCapturing}
							onPress={() => setCaturing(true)}
							style={[
								{
									width: 64,
									height: 64,
									borderRadius: 32,
									borderWidth: 4,
									borderColor: "white"
								},
								justifyContentCenter,
								alignItemsCenter,
								p10
							]}
						>
							<View
								style={[
									{ width: 52, height: 52, borderRadius: 26 },
									justifyContentCenter,
									alignItemsCenter,
									bgColor2
								]}
							>
								<Ionicons name="camera" color="white" size={30} />
							</View>
						</TouchableOpacity>
					</SafeAreaView>
					{showGrid && isAndroid && <Grid />}
				</CameraView>
				<ActionButton
					buttonColor={isMenuOpen ? COLOR5 : COLOR2}
					degrees={90}
					renderIcon={() => (
						<MaterialCommunityIcons
							name="dots-horizontal"
							size={40}
							color="white"
						/>
					)}
					onPressIn={() => setMenuOpen(true)}
					onReset={() => setMenuOpen(false)}
					// disabled={isFetching}
				>
					<ActionButton.Item
						buttonColor={"red"}
						title={t("button:cancel")}
						onPress={() => navigation.goBack()}
						textStyle={[fontBold, fs16]}
					>
						<MaterialCommunityIcons name="cancel" size={36} color="white" />
					</ActionButton.Item>
					{isNative && cameraType === "back" && (
						<ActionButton.Item
							buttonColor={COLOR2}
							title={
								flashMode === "on"
									? t("button:flashmodeoff")
									: t("button:flashmodeon")
							}
							onPress={() => setFlashMode(flashMode === "on" ? "off" : "on")}
							textStyle={[fontBold, fs16]}
							disabled={!isReady}
						>
							<Ionicons
								name={flashMode === "on" ? "flash-off" : "flash"}
								size={36}
								color="white"
							/>
						</ActionButton.Item>
					)}
					<ActionButton.Item
						buttonColor={COLOR2}
						title={showGrid ? t("button:hidegrid") : t("button:displaygrid")}
						onPress={() => setShowGrid(!showGrid)}
						textStyle={[fontBold, fs16]}
						disabled={!isReady}
					>
						<Ionicons name="grid" size={36} color="white" />
					</ActionButton.Item>
					{showSwitchCamera && (
						<ActionButton.Item
							buttonColor={COLOR2}
							title={
								cameraType === "front"
									? t("button:backcamera")
									: t("button:frontcamera")
							}
							onPress={() =>
								setCameraType(cameraType === "front" ? "back" : "front")
							}
							textStyle={[{ minHeight: 50 }, fontBold, fs16]}
							disabled={!isReady}
						>
							<Ionicons name="camera-reverse" size={36} color="white" />
						</ActionButton.Item>
					)}
				</ActionButton>
			</SafeAreaView>
		);
	}
}

const styles = StyleSheet.create({
	loading_container: {
		position: "absolute",
		left: 0,
		right: 0,
		top: 100,
		bottom: 0,
		alignItems: "center",
		justifyContent: "center"
	},
	grid: {
		borderColor: DARKGREY_COLOR,
		borderWidth: 1,
		borderBottomWidth: isAndroid ? 1 : 0,
		width: gridSize,
		height: gridSize
	},
	row: {
		borderColor: DARKGREY_COLOR,
		borderBottomWidth: 1,
		height: gridSize / 3,
		flexDirection: "row"
	},
	cell: {
		borderColor: DARKGREY_COLOR,
		borderRightWidth: 1,
		height: gridSize / 3,
		width: gridSize / 3
	}
});
